import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Offcanvas } from "react-bootstrap";
import keycloak from "../../keycloak";
import toast from "react-hot-toast";

const AddUserModal = ({
  show,
  onHide,
  userID,
  editMode,
  setEditMode = () => {},
}) => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    enabled: true,
    emailVerified: true,
  });

  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isTouched, setIsTouched] = useState({});

  const hiddenRoles = [
    "default-roles",
    "offline_access",
    "uma_authorization",
    "default-roles-master",
    // Add any other role names you want to hide
  ];

  useEffect(() => {
    fetchRealmRoles();
  }, []);

  useEffect(() => {
    validateForm();
  }, [formData, selectedRoles]);

  const fetchRealmRoles = async () => {
    setIsLoadingRoles(true);
    // console.log("authServerUrl",keycloak.authServerUrl);
    try {
      const response = await fetch(
        `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/roles`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();

      const filteredRoles = data.filter(role => 
        !hiddenRoles.includes(role.name) && 
        !role.name.startsWith('default-roles-') &&
        !role.name.startsWith('realm-')
      );
      
      setRoles(filteredRoles);
    } catch (error) {
      console.log("Error fetching realm roles:", error);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};
    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d).{8,}$/;

    if (!formData.username) errors.username = "Username is required.";
    if (!formData.email) errors.email = "Email is required.";
    if (!formData.firstName) errors.firstName = "First name is required.";
    if (!formData.lastName) errors.lastName = "Last name is required.";
    if (selectedRoles.length === 0) errors.roles = "Role is required.";
    if (!editMode) {
      if (!formData.password) {
        errors.password = "Password is required.";
      } else if (!passwordRegex.test(formData.password)) {
        errors.password =
          "Password must be at least 8 characters, include one uppercase letter, one special character, and one number.";
      }
    };

    setFormErrors(errors);
    setIsFormValid(Object.keys(errors).length === 0);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field) => {
    setIsTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const userDetails = {
      username: formData.username,
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      enabled: formData.enabled,
      emailVerified: formData.emailVerified,
      ...(!editMode &&
        formData.password && {
          credentials: [
            {
              type: "password",
              value: formData.password,
              temporary: false,
            },
          ],
        }),
    };

    if (editMode) {
      try {
        // const updateUrl = `https://oxdr-sso.invinsense.io/admin/realms/${keycloak.realm}/users/${userID}`;
        // const updateResponse = await fetch(updateUrl, {
        //   method: "PUT",
        //   headers: {
        //     "Content-Type": "application/json",
        //     Authorization: `Bearer ${keycloak.token}`,
        //   },
        //   body: JSON.stringify(userDetails),
        // });
        
        // if (!updateResponse.ok) {
        //   throw new Error(`HTTP error! Status: ${updateResponse.status}`);
        // };

      const updateUrl = `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}`;
      const updateResponse = await fetch(updateUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: JSON.stringify(userDetails),
      });

      if (!updateResponse.ok) {
        throw new Error(`HTTP error! Status: ${updateResponse.status}`);
      }

      // Get current role mappings
      const currentRolesResponse = await fetch(
        `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}/role-mappings/realm`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      );

      if (!currentRolesResponse.ok) {
        throw new Error(`Error fetching current roles! Status: ${currentRolesResponse.status}`);
      }

      const currentRoles = await currentRolesResponse.json();

      const rolesToRemove = currentRoles.filter(
        role => !hiddenRoles.includes(role.name) && 
               !role.name.startsWith('default-roles-') &&
               !role.name.startsWith('realm-')
      );

      // Remove all current roles
      if (currentRoles.length > 0) {
        const removeRolesUrl = `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}/role-mappings/realm`;
        const removeResponse = await fetch(removeRolesUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify(rolesToRemove),
        });

        if (!removeResponse.ok) {
          throw new Error(`Error removing roles! Status: ${removeResponse.status}`);
        }
      }

        // Update realm roles
        if (selectedRoles.length > 0) {
          const rolesToAssign = roles
            .filter((role) => selectedRoles.includes(role.id))
            .map((role) => ({
              id: role.id,
              name: role.name,
            }));

          const assignRolesUrl = `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}/role-mappings/realm`;
          const assignResponse = await fetch(assignRolesUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${keycloak.token}`,
            },
            body: JSON.stringify(rolesToAssign),
          });

          if (!assignResponse.ok) {
            throw new Error(`Error assigning roles! Status: ${assignResponse.status}`);
          }
        }

        toast.success("User updated successfully!");
        resetForm();
        onHide();
        setFormErrors({});
        setEditMode(false);
      } catch (error) {
        console.log("error", error);
        toast.error("Error updating user");
      }
      return;
    }

    try {
      const url = `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${keycloak.token}`,
        },
        body: JSON.stringify(userDetails),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const locationHeader = response.headers.get("Location");
      const userId = locationHeader.split("/").pop();

      if (selectedRoles.length > 0) {
        const rolesToAssign = roles
          .filter((role) => selectedRoles.includes(role.id))
          .map((role) => ({
            id: role.id,
            name: role.name,
          }));

        const assignRolesUrl = `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userId}/role-mappings/realm`;
        const assignResponse = await fetch(assignRolesUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
          body: JSON.stringify(rolesToAssign),
        });

        if (!assignResponse.ok) {
          throw new Error(`Error assigning roles! Status: ${assignResponse.status}`);
        }
      }

      toast.success("User has been created successfully!");
      resetForm();
      onHide();
      setFormErrors({});
      setEditMode(false);
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Error creating user");
    }
  };

  const handleClose = () => {
    onHide();
    resetForm();
    setFormErrors({});
    setEditMode(false);
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      enabled: true,
      emailVerified: true,
    });
    setSelectedRoles([]);
    setFormErrors({});
  };

  const getUserDetails = async () => {
    try {
      // Get user basic info
      const userResponse = await fetch(
        `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}?userProfileMetadata=${true}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      );

      // Get user realm role mappings
      const roleMappingsResponse = await fetch(
        `${keycloak.authServerUrl}/admin/realms/${keycloak.realm}/users/${userID}/role-mappings/realm`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${keycloak.token}`,
          },
        }
      );

      if (!userResponse.ok || !roleMappingsResponse.ok) {
        throw new Error("Failed to fetch user details");
      }

      const userData = await userResponse.json();
      const roleMappingsData = await roleMappingsResponse.json();

      const filteredRoleMappings = roleMappingsData.filter(
        role => !hiddenRoles.includes(role.name) && 
               !role.name.startsWith('default-roles-') &&
               !role.name.startsWith('realm-')
      );

      setFormData((prevData) => ({
        ...prevData,
        username: userData.username || "",
        email: userData.email || "",
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        enabled: userData.enabled ?? true,
        emailVerified: userData.emailVerified ?? true,
      }));

      const mappedRoleIds = filteredRoleMappings.map((role) => role.id);
      setSelectedRoles(mappedRoleIds);

    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    if (editMode && userID) {
      getUserDetails();
    } else {
      resetForm();
      setFormErrors({});
    }
  }, [editMode]);

  useEffect(() => {
    setFormErrors({});
  }, [show]);

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="custom-offcanvas"
      style={{ width: "600px" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {editMode ? "Edit User" : "Create New User"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={handleSubmit}>
        {!editMode && (
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                className="form-control-light"
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "0.5rem",
                  color: "#212529",
                  width: "100%",
                }}
                disabled={editMode}
                isInvalid={!!formErrors.username && isTouched.username}
                onBlur={() => handleBlur("username")}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.username}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-control-light"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "0.5rem",
                color: "#212529",
                width: "100%",
              }}
              isInvalid={!!formErrors.email && isTouched.email}
              onBlur={() => handleBlur("email")}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.email}
            </Form.Control.Feedback>
          </Form.Group>

          {!editMode && (
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="form-control-light"
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "0.5rem",
                  color: "#212529",
                  width: "100%",
                }}
                disabled={editMode}
                isInvalid={!!formErrors.password && isTouched.password}
                onBlur={() => handleBlur("password")}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              required
              className="form-control-light"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "0.5rem",
                color: "#212529",
                width: "100%",
              }}
              isInvalid={!!formErrors.firstName && isTouched.firstName}
              onBlur={() => handleBlur("firstName")}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.firstName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              required
              className="form-control-light"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                padding: "0.5rem",
                color: "#212529",
                width: "100%",
              }}
              isInvalid={!!formErrors.lastName && isTouched.lastName}
              onBlur={() => handleBlur("lastName")}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.lastName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Select Roles</Form.Label>
            <div
              className="role-selection-container"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                maxHeight: "180px",
                height: "180px",
                overflowY: "auto",
                padding: "0.5rem",
              }}
              isInvalid={!!formErrors.roles}
              // onBlur={() => handleBlur("roles")}
            >
              {roles.length > 0 ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.25rem 0.5rem",
                      borderBottom: "1px solid #dee2e6",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      id="select-all-roles"
                      label="Select All"
                      checked={selectedRoles.length === roles.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRoles(roles.map((role) => role.id));
                        } else {
                          setSelectedRoles([]);
                        }
                      }}

                    />
                  </div>
                  {roles.map((role) => (
                    <div
                      key={role.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.25rem 0.5rem",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`role-${role.id}`}
                        label={role.name}
                        checked={selectedRoles.includes(role.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoles([...selectedRoles, role.id]);
                          } else {
                            setSelectedRoles(
                              selectedRoles.filter((id) => id !== role.id)
                            );
                          }
                        }}
                      />
                      {role.description && (
                        <span
                          style={{
                            marginLeft: "0.5rem",
                            fontSize: "0.875rem",
                            color: "#6c757d",
                          }}
                        >
                          ({role.description})
                        </span>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div
                  style={{
                    padding: "1rem",
                    textAlign: "center",
                    color: "#6c757d",
                    marginTop: "50px",
                  }}
                >
                  {isLoadingRoles
                    ? "Fetching roles..."
                    : "Select a client to view available roles"}
                </div>
              )}
            </div>
            {selectedRoles.length > 0 ? (
              <div
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.875rem",
                  color: "#6c757d",
                }}
              >
                {selectedRoles.length} role(s) selected
              </div>
            ) : (
              <div
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.875rem",
                  color: "white",
                }}
              >
                No role(s) selected
              </div>
            )}
            <Form.Control.Feedback type="invalid">
              {formErrors.roles}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="light"
              onClick={handleClose}
              style={{
                border: "1px solid #dee2e6",
                color: "#6c757d",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              style={{
                backgroundColor: "#0d6efd",
                border: "none",
              }}
              disabled={!isFormValid}
            >
              {editMode ? "Update User" : "Create User"}
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddUserModal;
